import { InjectProperty } from '@jack-henry/frontend-utils/di';
import { CheckException, CheckExceptionService } from '@treasury/domain/arp';
import { TmBaseComponent } from '@treasury/presentation';
import { checkIcon } from '@treasury/presentation/assets/icons';
import { noOp } from '@treasury/utils';
import { columnBodyRenderer } from '@vaadin/grid/lit.js';
import { GridColumn } from '@vaadin/grid/src/vaadin-grid-column';
import { css, html, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { CheckExceptionVm } from '../../view-models/checkException';

export const tagName = 'check-exceptions-table';
@customElement(tagName)
export class CheckExceptionsTable extends TmBaseComponent {
    @property()
    public checkExceptions: CheckException[] = [];

    @property({ type: Boolean, reflect: true, attribute: 'readonly' })
    public readonly = false;

    @property()
    public setSelectedException: (exception: CheckException) => void = noOp;

    protected readonly verifiedPropNames: (keyof this)[] = ['checkExceptions'];

    @InjectProperty()
    declare private readonly checkExceptionService: CheckExceptionService;

    public render() {
        if (!this.checkExceptions?.length) {
            return nothing;
        }
        return html`
            <vaadin-grid
                .items=${this.checkExceptions}
                all-rows-visible
                class=${classMap({
                    'w-full': true,
                    readonly: !!this.readonly,
                })}
                .cellPartNameGenerator=${this.cellPartNameGen}
            >
                <vaadin-grid-column
                    width="auto"
                    flex-grow="0"
                    class="decision-toggle"
                    ${columnBodyRenderer(
                        (exception: CheckException) => html`
                            <decision-toggle
                                class="flex h-full items-center"
                                .exception=${exception}
                                ?readonly=${this.readonly}
                                .decisionStatus=${exception.decisionStatus}
                                .modified=${exception.decisionModified}
                            ></decision-toggle>
                        `
                    )}
                ></vaadin-grid-column>
                <vaadin-grid-column
                    ${columnBodyRenderer((exception: CheckException) => {
                        const vm = new CheckExceptionVm(exception);
                        const checkNumber = exception.checkNumber
                            ? html` ${exception.checkNumber} | `
                            : nothing;
                        const checkImageIcon = exception.checkImageNumber
                            ? html`<jhd-icon .icon=${checkIcon}></jhd-icon>`
                            : nothing;
                        return html`
                            <button
                                class="py-2 text-left w-full h-full flex justify-between"
                                @click=${() => this.setSelectedException(exception)}
                            >
                                <div>
                                    <div class="font-bold text-sm">${vm.accountNumber}</div>
                                    <div class="text-xs text-left">
                                        ${checkNumber} Paid: ${vm.paidAmount}
                                    </div>
                                </div>
                                <div class="flex flex-col items-end gap-1 pr-1">
                                    ${exception.exceptionReason
                                        ? html`<tm-badge>${exception.exceptionReason}</tm-badge>`
                                        : nothing}
                                    <div class="text-right">${checkImageIcon}</div>
                                </div>
                            </button>
                        `;
                    })}
                >
                </vaadin-grid-column>
            </vaadin-grid>
        `;
    }

    private cellPartNameGen(col: GridColumn, val: { item: CheckException }) {
        const border = col.classList.contains('decision-toggle') ? 'decision-toggle' : '';
        return val.item.showModified ? `modified ${border}` : border;
    }

    public static get styles() {
        return [
            css`
                :host {
                    --lumo-space-xs: 0;
                    --lumo-space-m: 0;
                    --grid-modified-row: var(--tm-color-gray-light-30);
                    display: block;
                }
                :host([theme='dark']) {
                    --grid-modified-row: var(--jh-color-gray-900);
                }
                vaadin-grid {
                    --vaadin-grid-cell: {
                        padding: 0;
                        height: 100px;
                    };
                }
                vaadin-grid {
                    max-height: calc(var(--below-navbar-view-height) - 282px);
                }
                vaadin-grid.after-cutoff {
                    max-height: calc(var(--below-navbar-view-height) - 126px);
                }
                vaadin-grid.readonly {
                    max-height: calc(var(--below-navbar-view-height) - 126px);
                }
                vaadin-grid-cell-content {
                    height: 100%;
                }
                vaadin-grid::part(modified) {
                    background-color: var(--grid-modified-row);
                }
                vaadin-grid::part(read-only) {
                    background-color: var(--container-primary);
                }
                vaadin-grid::part(decision-toggle) {
                    border-color: transparent;
                }
            `,
        ];
    }
}

declare global {
    interface HTMLElementTagNameMap {
        [tagName]: CheckExceptionsTable;
    }
}
